// apps/web/src/lib/image.ts
// Downscale an image File to a small JPEG and return base64 (no data: prefix).
// Keeps the upload payload tiny so the vision estimate is fast and cheap.

// Must stay <= the server's MAX_IMAGE_BYTES (3 MB) in functions/lib/ai-estimate.ts —
// a higher-res/quality encode of a dense photo can otherwise be rejected with 400.
const MAX_UPLOAD_BYTES = 3 * 1024 * 1024;

// 1024 px, not 1536. A vision model reads an image as 512 px tiles, so 1024 is
// a 2×2 grid where 1536 is 3×3 — more than twice the tiles, and roughly twice
// the bytes on the wire, for detail finer than "what is on this plate". On
// mobile data the upload is the only part of estimate latency we control.
export async function downscaleToJpegBase64(file: File, maxPx = 1024, quality = 0.85): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxPx / Math.max(bitmap.width, bitmap.height));
    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas unavailable");
    ctx.drawImage(bitmap, 0, 0, w, h);
    // Encode at the requested quality, stepping down only if the payload would
    // exceed the server cap (base64 length × 0.75 ≈ decoded bytes).
    let b64 = "";
    for (const q of [quality, 0.8, 0.7, 0.6]) {
      b64 = canvas.toDataURL("image/jpeg", q).replace(/^data:image\/jpeg;base64,/, "");
      if (b64.length * 0.75 <= MAX_UPLOAD_BYTES) return b64;
    }
    return b64; // best effort: the smallest encode we produced
  } finally {
    bitmap.close();
  }
}
